import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { LoginParams } from 'auth/lib/provider';
import { captureException } from '@sentry/react';
import { ApolloClient } from '@apollo/client';

export type UseAccessTokenStore = {
	client: ApolloClient<any> | null;
	setClient: (client: ApolloClient<any> | null) => void;
	accessToken: string | null;
	refreshAccessToken: (refreshUri: string, onSessionExpired?: () => void) => () => Promise<string>;
	login: (loginUri: string, client?: ApolloClient<any>) => (params: LoginParams) => Promise<void>
	logout: (logoutUri: string) => () => Promise<void>
	setAccessToken: (accessToken: string | null) => void;
};

class LoginError extends Error {
	response: { [key: string]: any };

	constructor(response?: { [key: string]: any }) {
		super('loginError');
		this.response = response ?? {};
	}
}

const useAccessTokenStore = create<UseAccessTokenStore>()(persist((set) => ({
	accessToken:    null,
	client:         null,
	setClient:      (client) => set({ client }),
	setAccessToken: (accessToken) => set({ accessToken }),
	login:          (loginUri, client) => async (params) => {
		const {
			username, password, policy, code, saveBrowser,
		} = params;
		const fetchResponse = await fetch(loginUri, {
			method:      'POST',
			credentials: 'include',
			body:        JSON.stringify({
				username, password, policy, code, saveBrowser,
			}),
			headers: { 'Content-Type': 'application/json' },
		});

		const responseJSON = await fetchResponse.json();

		if (!fetchResponse.ok) {
			throw new LoginError(responseJSON);
		}

		const accessToken = responseJSON?.access ?? null;

		client?.resetStore?.();
		set({ accessToken });
	},
	logout: (logoutUri) => async () => {
		set({ accessToken: null });

		try {
			await fetch(logoutUri, { method: 'POST', credentials: 'include' });
		} catch (e) {
			const isTestEnv = typeof window !== 'undefined' && (window as Window & { Cypress?: unknown }).Cypress;

			if (!isTestEnv) {
				captureException(e);
			}
		}
	},
	refreshAccessToken: (refreshUri, onSessionExpired) => async () => {
		const fetchResponse = await fetch(refreshUri, {
			method:      'POST',
			credentials: 'include',
		});

		if (!fetchResponse.ok) {
			onSessionExpired?.();
			set({ accessToken: null });
		}

		const responseJSON = await fetchResponse.json();
		const accessToken = responseJSON?.access ?? null;

		set({ accessToken });

		return accessToken;
	},
}), {
	name:       'access-token',
	partialize: ({ accessToken }) => ({ accessToken }),
}));

export default useAccessTokenStore;
